import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { SingleSelectAutocomplete } from "@flat-ui";
import { SEARCH_DEBOUNCE_TIME } from "@hooks/use-debounced-search-text";
import { useLanguage } from "@hooks/use-language";
import { ARCGIS_LANGUAGE_CODES_MAP, geocodeAddress, getAddressSuggestions, isGeocodeAbortError } from "@utils/geocoding-utils";
import { useDebounce } from "@utils/time-utils";
import { useCallback, useEffect, useRef, useState } from "react";
/**
 * @returns A component that displays an autocomplete input field for addresses.
 * The component uses a geocoding service to get address suggestions and geocode addresses.
 */
export function AddressAutocomplete({
  input,
  setInput,
  variant = "outlined",
  isDisabled,
  placeholder,
  shouldAutoFocus = true,
  sx,
  onGeocodedAddressSelect,
  onCustomAddressSelect,
  onLoadingSuggestionsChange,
  onLoadingGeocodedAddressChange,
  onCancel
}) {
  const {
    currentLanguage
  } = useLanguage();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const debouncedInput = useDebounce(input, SEARCH_DEBOUNCE_TIME);
  const lastInputReason = useRef("reset");
  const [suggestions, setSuggestions] = useState([]);
  const suggestionsAbort = useRef(null);
  const langCode = ARCGIS_LANGUAGE_CODES_MAP[currentLanguage.code];
  const options = useCallback(() => suggestions.map(suggestion => ({
    title: suggestion.label,
    subtitle: "",
    value: suggestion.key
  })), [suggestions]);

  /**
   * Gets address suggestions for the given input text using the geocoding service.
   * @param text The input text for which to get address suggestions.
   */
  const getSuggestions = useCallback(async text => {
    onLoadingSuggestionsChange?.("pending");
    try {
      // Abort any ongoing suggestions request before starting a new one.
      // This can help prevent any potential race conditions where older requests
      // return after newer requests and overwrite the suggestions with outdated data.
      suggestionsAbort.current?.abort();
      suggestionsAbort.current = new AbortController();
      const response = await getAddressSuggestions({
        text,
        signal: suggestionsAbort.current.signal
      });
      setSuggestions(response);
      onLoadingSuggestionsChange?.("succeeded");
    } catch (error) {
      const isAbortError = isGeocodeAbortError(error);
      // Only show an error toast if the error is not caused by aborting the request.
      // An aborted request is expected to happen.
      if (!isAbortError) {
        handleErrorWithToast({
          id: `getAddressSuggestions-${Date.now().toString()}`,
          title: "Error loading address suggestions",
          error
        });
      }
      onLoadingSuggestionsChange?.("rejected");
    }
  }, [handleErrorWithToast, onLoadingSuggestionsChange]);

  /**
   * Gets the geocoded address from the address suggestion selected by the user via a geocoding service.
   * @param addressSuggestion - The address suggestion.
   */
  const getGeocodedAddress = useCallback(async addressSuggestion => {
    onLoadingGeocodedAddressChange?.("pending");
    try {
      const geocodedAddress = await geocodeAddress({
        addressSuggestion,
        langCode
      });
      onGeocodedAddressSelect?.(geocodedAddress);
      onLoadingGeocodedAddressChange?.("succeeded");
    } catch (error_0) {
      handleErrorWithToast({
        id: `getGeocodedAddress-${Date.now().toString()}`,
        title: "Error loading the geocoded address",
        error: error_0
      });
      onLoadingGeocodedAddressChange?.("rejected");
    }
  }, [handleErrorWithToast, langCode, onGeocodedAddressSelect, onLoadingGeocodedAddressChange]);

  /** Triggers getting address suggestions when the debounced input changes and the input is not empty */
  useEffect(() => {
    const sanitizedInput = debouncedInput.trim();
    if (sanitizedInput.length > 0 && lastInputReason.current === "input") {
      void getSuggestions(sanitizedInput);
    }
  }, [debouncedInput, getSuggestions]);
  return <SingleSelectAutocomplete freeSolo value={null} showIcon={false} inputValue={input} options={options()} hasAutoFocus={shouldAutoFocus} placeholder={placeholder} isDisabled={isDisabled} inputVariant={variant} onInputChange={(newValue, reason) => {
    lastInputReason.current = reason;
    // After the user selects an address suggestion, we still need to fetch the
    // corresponding geocoded address to get the "real" address text that will be
    // displayed in the input. For this reason we skip setting the input here
    // and instead let the getGeocodedAddress function set it.
    if (reason !== "selectOption" && reason !== "reset") {
      setInput(newValue);
    }
    // Clear address suggestions if the sanitized input is cleared
    if (newValue.trim().length === 0) {
      setSuggestions([]);
    }
  }} sx={sx}
  // Addresses are long: let them wrap rather than end in an ellipsis
  wrapOptionText
  // Bypass the default filter of the MUI Autocomplete component. This is needed
  // because the default behavior filters out suggestions that don't contain
  // the input value. But for our use case we want show all suggestions returned
  // by the geocoding service API, even if they don't contain the input value.
  filterOptions={currentOptions => currentOptions} onChange={(newValue_0, reason_0) => {
    // Geocode the address if the user selected a suggestion from the dropdown
    if (typeof newValue_0 !== "string" && newValue_0 !== null && reason_0 === "selectOption") {
      const selectedSuggestion = suggestions.find(suggestion_0 => suggestion_0.key === newValue_0.value);
      if (selectedSuggestion) {
        void getGeocodedAddress(selectedSuggestion);
      }
    }

    // Check if the user selected a custom address by pressing Enter without selecting a suggestion
    if (typeof newValue_0 === "string" && reason_0 === "createOption") {
      onCustomAddressSelect?.(newValue_0);
    }
  }} onKeyDown={event => {
    if (event.key === "Escape") {
      onCancel?.();
    }

    // MUI Autocomplete does not trigger the onChange event when the user presses Enter
    // on an empty input. Since we want to allow the user to set an empty string as custom address
    // we need to handle this case here.
    const sanitizedInput_0 = input.trim();
    if (event.key === "Enter" && sanitizedInput_0.length === 0) {
      onCustomAddressSelect?.(sanitizedInput_0);
    }
  }} noOptionsText="No addresses found" />;
}